import type {
  FinderData,
  GroupDetailData,
  HouseholdMember,
  InquiryPerson,
  InquiryResult,
  JoinFormData,
  JoinResult,
  LocationGroup,
  MyGroupsData,
} from './types';
import { getMpWidgetToken } from './campus';
import { mpFetch } from '@church/nextjs-ui/website/mp-session';

// Vite bakes these at build time via `define` in vite.config.ts.
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

function headers(): Record<string, string> {
  const h: Record<string, string> = {};
  if (BAKED_API_KEY) h['x-api-key'] = BAKED_API_KEY;
  if (BAKED_APP_SLUG) h['x-app-slug'] = BAKED_APP_SLUG;
  return h;
}

function base(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/$/, '');
}

const RETRY_DELAYS_MS = [1500, 3000];

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

/**
 * GET with two retries on a 5xx or network error: this runs on a public page,
 * and a brief edge hiccup should not show visitors an error card.
 */
async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1], signal);
    try {
      const res = await fetch(url, { headers: headers(), signal });
      if (res.ok) return (await res.json()) as T;
      lastError = new Error(`${url} responded ${res.status}`);
      if (res.status < 500) break; // a 4xx will not fix itself
    } catch (err) {
      if (signal?.aborted) throw err;
      lastError = err;
    }
  }
  throw lastError;
}

/** The whole Life Groups catalogue plus filter options. */
export function fetchFinderData(apiBaseUrl: string, signal?: AbortSignal): Promise<FinderData> {
  return getJson<FinderData>(`${base(apiBaseUrl)}/api/groups`, signal);
}

/** Campuses grouped by location, with each location's brand SVG. */
export function fetchLocations(apiBaseUrl: string, signal?: AbortSignal): Promise<LocationGroup[]> {
  return getJson<LocationGroup[]>(`${base(apiBaseUrl)}/api/locations`, signal);
}

/**
 * The signed-in visitor's default campus (Contacts.Web_Congregation_ID,
 * falling back to their household's campus). Null when not signed in.
 */
export async function fetchHomeCampus(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<number | null> {
  const token = getMpWidgetToken();
  if (!token) return null;
  try {
    const res = await mpFetch(`${base(apiBaseUrl)}/api/user/home-campus`, {
      headers: { ...headers(), Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { congregationId: number | null };
    return data.congregationId ?? null;
  } catch {
    return null;
  }
}

/** Persist a campus choice to MP for the signed-in visitor. No-op when signed out. */
export async function setHomeCampus(apiBaseUrl: string, congregationId: number): Promise<boolean> {
  const token = getMpWidgetToken();
  if (!token) return false;
  try {
    const res = await mpFetch(`${base(apiBaseUrl)}/api/set-home-campus`, {
      method: 'POST',
      headers: {
        ...headers(),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ congregationId }),
    });
    return res.ok;
  } catch (err) {
    console.error('Group finder: could not save home campus', err);
    return false;
  }
}

/**
 * The signed-in visitor's own Life Groups with the member-only extras
 * (real address, roster, Connect link). Null when signed out or on error;
 * the finder works without it.
 */
export async function fetchMyGroups(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<MyGroupsData | null> {
  const token = getMpWidgetToken();
  if (!token) return null;
  try {
    const res = await mpFetch(`${base(apiBaseUrl)}/api/groups/me`, {
      headers: { ...headers(), Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as MyGroupsData;
  } catch {
    return null;
  }
}

/** One group for the details page; null when the catalogue does not list it. */
export async function fetchGroup(
  apiBaseUrl: string,
  groupId: number,
  signal?: AbortSignal
): Promise<GroupDetailData | null> {
  try {
    return await getJson<GroupDetailData>(`${base(apiBaseUrl)}/api/groups/${groupId}`, signal);
  } catch (err) {
    if (err instanceof Error && /responded 404/.test(err.message)) return null;
    throw err;
  }
}

/** The signed-in person and the other adults in their household. Null when signed out. */
export async function fetchHousehold(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<HouseholdMember[] | null> {
  const token = getMpWidgetToken();
  if (!token) return null;
  try {
    const res = await mpFetch(`${base(apiBaseUrl)}/api/user/household`, {
      headers: { ...headers(), Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { members: HouseholdMember[] };
    return data.members;
  } catch (err) {
    if (signal?.aborted) throw err;
    return null;
  }
}

export class InquiryError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
  }
}

/** Ask to join a group. Requires the MP widget token. */
export async function submitInquiry(
  apiBaseUrl: string,
  groupId: number,
  input: { people: InquiryPerson[]; comments: string; website?: string }
): Promise<InquiryResult> {
  const token = getMpWidgetToken();
  if (!token) throw new InquiryError('Sign in to inquire', 401);
  const res = await mpFetch(`${base(apiBaseUrl)}/api/groups/${groupId}/inquiries`, {
    method: 'POST',
    headers: { ...headers(), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    let message = 'Could not send your inquiry';
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* no body */
    }
    throw new InquiryError(message, res.status);
  }
  return (await res.json()) as InquiryResult;
}

/** Withdraw a waiting inquiry (one of the household's). */
export async function cancelInquiry(
  apiBaseUrl: string,
  groupId: number,
  inquiryId: number
): Promise<void> {
  const token = getMpWidgetToken();
  if (!token) throw new InquiryError('Sign in to cancel an inquiry', 401);
  const res = await mpFetch(`${base(apiBaseUrl)}/api/groups/${groupId}/inquiries`, {
    method: 'DELETE',
    headers: { ...headers(), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ inquiryId }),
  });
  if (!res.ok) {
    let message = 'Could not cancel the inquiry';
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* no body */
    }
    throw new InquiryError(message, res.status);
  }
}

/** The "Join a Group" form as MP defines it. */
export function fetchJoinForm(apiBaseUrl: string, signal?: AbortSignal): Promise<JoinFormData> {
  return getJson<JoinFormData>(`${base(apiBaseUrl)}/api/groups/join`, signal);
}

/** "Help me find a group": one Form_Responses row. Requires the MP widget token. */
export async function submitJoinForm(
  apiBaseUrl: string,
  input: { answers: Record<number, string>; website?: string }
): Promise<JoinResult> {
  const token = getMpWidgetToken();
  if (!token) throw new InquiryError('Sign in so we know who to reach', 401);
  const res = await mpFetch(`${base(apiBaseUrl)}/api/groups/join`, {
    method: 'POST',
    headers: { ...headers(), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    let message = 'Could not send your request';
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* no body */
    }
    throw new InquiryError(message, res.status);
  }
  return (await res.json()) as JoinResult;
}
